import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import { tokens } from "../../../../theme";
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useApi from '../../../shared/hooks/useApi';
import * as XLSX from 'xlsx';
import CostReport from './CostReport';
import AfrrReservation from './AfrrReservation';

const Report = ({ setLoading, triggerAlert }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [reportData, setReportData] = useState([]);
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(7, 'day'));
  const [dateTo, setDateTo] = useState(dayjs());
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const { apiGet } = useApi();
  const [tabValue, setTabValue] = useState(0);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await apiGet(
        "UX_HTTP_API",
        `/esid-24-003/get-reporting?from=${dateFrom.format('YYYY-MM-DD')}&to=${dateTo.format('YYYY-MM-DD')}`
      );
      response.sort((a, b) => new Date(b.message.metaData.calculationTime.value) - new Date(a.message.metaData.calculationTime.value));
      const headers = [response[0]?.message?.metaData?.calculationTime?.label, response[0]?.message?.metaData?.calculationPeriodFromDate?.label, response[0]?.message?.metaData?.calculationPeriodToDate?.label, response[0]?.message?.costs?.baseline?.label, response[0]?.message?.costs?.optimised?.label, response[0]?.message?.costs?.savings?.label, response[0]?.message?.marketPrices?.average?.label, response[0]?.message?.marketPrices?.profileWeighted?.label, response[0]?.message?.energy?.totalConsumption?.label || "Total Consumption"];
      const units = [response[0]?.message?.metaData?.calculationTime?.unit, response[0]?.message?.metaData?.calculationPeriodFromDate?.unit, response[0]?.message?.metaData?.calculationPeriodToDate?.unit, response[0]?.message?.costs?.baseline?.unit, response[0]?.message?.costs?.optimised?.unit, response[0]?.message?.costs?.savings?.unit, response[0]?.message?.marketPrices?.average?.unit, response[0]?.message?.marketPrices?.profileWeighted?.unit, response[0]?.message?.energy?.totalConsumption?.unit || "Total Consumption"];
      const rows = []
      let tempElementValues = []
      response.map((row, index) => {
        tempElementValues.push(row?.message?.metaData?.calculationTime?.value)
        tempElementValues.push(row?.message?.metaData?.calculationPeriodFromDate?.value)
        tempElementValues.push(row?.message?.metaData?.calculationPeriodToDate?.value)
        tempElementValues.push(row?.message?.costs?.baseline?.value)
        tempElementValues.push(row?.message?.costs?.optimised?.value)
        tempElementValues.push(row?.message?.costs?.savings?.value)
        tempElementValues.push(row?.message?.marketPrices?.average?.value)
        tempElementValues.push(row?.message?.marketPrices?.profileWeighted?.value)
        tempElementValues.push(row?.message?.energy?.totalConsumption?.value)
        rows.push(tempElementValues)
        tempElementValues = []
      })

      setHeaders(headers)
      setRows(rows)
      setUnits(units)
      setReportData(response || []);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [dateFrom, dateTo]);

  const handleDownloadSingle = (row) => {
    try {
      let tempHeaders = [];
      let fileName = row[0];
      headers.map((header, index) => {
        units[index] !== "None"
          ? tempHeaders.push(header + (units[index] !== null ? "-" + units[index] : ""))
          : tempHeaders.push(header || "");
      });

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet([tempHeaders, row]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      // Write file
      XLSX.writeFile(wb, `report_${fileName}.xlsx`);
      triggerAlert("success", "Report downloaded successfully");
    } catch (error) {
      console.error('Error downloading single report:', error);
      triggerAlert("error", "Error downloading single report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = () => {
    try {
      let tempHeaders = [];
      headers.map((header, index) => {
        units[index] !== "None" ? tempHeaders.push(header + "-" + units[index]) : tempHeaders.push(header)
      });

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet([tempHeaders, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "All Reports");

      // Write file
      XLSX.writeFile(wb, `all_reports_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.xlsx`);
      triggerAlert("success", "All reports downloaded successfully");
    } catch (error) {
      console.error('Error downloading all reports:', error);
      triggerAlert("error", "Error downloading all reports");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box m={4} ml={10}>
      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab label="Cost Report" />
        <Tab label="aFRR Report" />
      </Tabs>

      {tabValue === 0 && (
        <CostReport colors={colors} reportData={reportData} headers={headers} rows={rows} units={units} dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} handleDownloadAll={handleDownloadAll} handleDownloadSingle={handleDownloadSingle} />
      )}

      {tabValue === 1 && (
        <AfrrReservation colors={colors} setLoading={setLoading} triggerAlert={triggerAlert} handleDownloadAll={handleDownloadAll} handleDownloadSingle={handleDownloadSingle} />
      )}
    </Box>
  );
};

export default Report;